import React, { useState } from 'react';
import { AreaOfStudy } from '../data/areaOfStudyData';
import { jobTitleDetails, JobTitleInfo } from '../data/jobTitleDetails';
import { BriefcaseIcon, TrendingUpIcon, DollarSignIcon, CheckCircleIcon, ExternalLinkIcon, ChevronDownIcon, GraduationCapIcon } from 'lucide-react';
interface ContentSectionProps {
  areaData: AreaOfStudy;
  selectedAreaId: number;
  onNavigate?: (page: string, slug?: string) => void;
}
function JobTitleCard({
  job,
  fallbackTitle
}: {
  job?: JobTitleInfo;
  fallbackTitle: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  if (!job) {
    return <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-70">
        <div className="w-2 h-2 rounded-full bg-[#3b82f6]" data-id="element-71"></div>
        <span className="font-medium" data-id="element-72">{fallbackTitle}</span>
      </div>;
  }
  return <div className="bg-gray-50 rounded-lg border border-gray-100 overflow-hidden" data-id="element-73">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-100/50 transition-colors" data-id="element-74">
        <div className="flex items-center gap-2" data-id="element-75">
          <div className="w-2 h-2 rounded-full bg-[#3b82f6] flex-shrink-0" data-id="element-76"></div>
          <span className="font-medium text-gray-900" data-id="element-77">{job.title}</span>
        </div>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-78" />
      </button>

      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-79">
        <div className="px-3 pb-4 pt-1 border-t border-gray-200" data-id="element-80">
          <p className="text-sm text-gray-600 leading-relaxed mb-3" data-id="element-81">
            {job.description}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-id="element-82">
            <div className="bg-white rounded-md p-2.5 border border-gray-100" data-id="element-83">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-84">
                <DollarSignIcon className="w-3.5 h-3.5 text-green-600" data-id="element-85" />
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-86">
                  Median Salary
                </span>
              </div>
              <span className="text-sm font-bold text-gray-900" data-id="element-87">
                {job.medianSalary}
              </span>
            </div>

            <div className="bg-white rounded-md p-2.5 border border-gray-100" data-id="element-88">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-89">
                <TrendingUpIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-90" />
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-91">
                  Job Growth
                </span>
              </div>
              <span className="text-xs font-semibold text-gray-900" data-id="element-92">
                {job.growthRate}
              </span>
            </div>

            <div className="bg-white rounded-md p-2.5 border border-gray-100" data-id="element-93">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-94">
                <GraduationCapIcon className="w-3.5 h-3.5 text-purple-600" data-id="element-95" />
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-96">
                  Education
                </span>
              </div>
              <span className="text-xs font-semibold text-gray-900" data-id="element-97">
                {job.education}
              </span>
            </div>
          </div>

          <p className="text-[11px] text-gray-400 mt-3 flex items-center gap-1" data-id="element-98">
            <ExternalLinkIcon className="w-3 h-3 flex-shrink-0" data-id="element-99" />
            <span data-id="element-100">
              Source:{' '}
              <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600" data-id="element-101">
                U.S. Bureau of Labor Statistics
              </a>
              {', '}
              <a href="https://www.bls.gov/oes/current/oes_nat.htm" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600" data-id="element-102">
                Occupational Employment &amp; Wage Statistics
              </a>
            </span>
          </p>
        </div>
      </div>
    </div>;
}
export function ContentSection({
  areaData,
  selectedAreaId,
  onNavigate
}: ContentSectionProps) {
  const jobDetails = jobTitleDetails[areaData.id] || [];
  return <section className="w-full bg-white border-t border-gray-200 py-16" data-id="element-103">
      <div className="max-w-4xl mx-auto px-4" data-id="element-104">
        <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-105">
          Is a Degree in {areaData.name} Right for You?
        </h2>

        <div className="prose prose-lg max-w-none text-gray-700" data-id="element-106">
          <p className="text-xl leading-relaxed mb-10 text-gray-600" data-id="element-107">
            {areaData.whyStudy}
          </p>

          <div className="mb-12" data-id="element-108">
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100" data-id="element-109">
              <div className="flex items-center gap-3 mb-4" data-id="element-110">
                <div className="bg-blue-100 p-2 rounded-lg text-blue-600" data-id="element-111">
                  <TrendingUpIcon className="w-6 h-6" data-id="element-112" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 m-0" data-id="element-113">
                  Career Outlook
                </h3>
              </div>
              <p className="text-sm m-0" data-id="element-114">{areaData.careerOutlook}</p>
              <p className="text-[11px] text-blue-600 mt-3 m-0 flex items-center gap-1" data-id="element-115">
                <ExternalLinkIcon className="w-3 h-3" data-id="element-116" />
                <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-800" data-id="element-117">
                  Source: U.S. Bureau of Labor Statistics, Occupational Outlook
                  Handbook
                </a>
              </p>
            </div>
          </div>

          <div className="mb-12" data-id="element-118">
            <div className="flex items-center gap-3 mb-2" data-id="element-119">
              <BriefcaseIcon className="w-6 h-6 text-gray-400" data-id="element-120" />
              <h3 className="text-2xl font-bold text-gray-900 m-0" data-id="element-121">
                Common Job Titles
              </h3>
            </div>
            <p className="text-sm text-gray-500 mb-5 pl-9" data-id="element-122">
              Click any job title to see salary data, growth outlook, and
              education requirements.
            </p>

            <div className="grid grid-cols-1 gap-3" data-id="element-123">
              {areaData.jobTitles.map((title, idx) => {
              const jobInfo = jobDetails.find(j => j.title === title);
              return <JobTitleCard key={idx} job={jobInfo} fallbackTitle={title} data-id="element-124" />;
            })}
            </div>

            <p className="text-[11px] text-gray-400 mt-3 pl-1 flex items-center gap-1" data-id="element-125">
              <ExternalLinkIcon className="w-3 h-3" data-id="element-126" />
              Salary and growth data sourced from the{' '}
              <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600" data-id="element-127">
                Bureau of Labor Statistics
              </a>
            </p>
          </div>

          <div className="bg-gray-900 text-white rounded-2xl p-8 md:p-10 shadow-xl" data-id="element-128">
            <h3 className="text-2xl font-bold text-white mb-4 mt-0" data-id="element-129">
              What to Look For in a Program
            </h3>
            <div className="flex items-start gap-4" data-id="element-130">
              <CheckCircleIcon className="w-8 h-8 text-[#22c55e] flex-shrink-0 mt-1" data-id="element-131" />
              <p className="text-gray-300 text-lg leading-relaxed m-0" data-id="element-132">
                {areaData.whatToLookFor}
              </p>
            </div>
          </div>

          {/* BLS Citation Footer */}
          <div className="mt-10 pt-6 border-t border-gray-200" data-id="element-133">
            <p className="text-xs text-gray-400 leading-relaxed" data-id="element-134">
              Employment projections and salary data are sourced from the{' '}
              <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-gray-500 underline hover:text-gray-700" data-id="element-135">
                U.S. Bureau of Labor Statistics (BLS) Occupational Outlook
                Handbook
              </a>{' '}
              and{' '}
              <a href="https://www.bls.gov/oes/" target="_blank" rel="noopener noreferrer" className="text-gray-500 underline hover:text-gray-700" data-id="element-136">
                Occupational Employment and Wage Statistics (OEWS)
              </a>
              . Data reflects national averages and may vary by location,
              experience, and employer. Projections cover the 2022–2032 decade.
              Last reviewed March 2026.
            </p>
          </div>
        </div>
      </div>
    </section>;
}